import { Injectable } from '@angular/core';
import { AuthService } from '../../core/service/auth/auth.service';
import { environment } from '../../../environments/environment';
import { AppConfigService } from 'src/app/appConfig.service';

export interface AgentChatRequest {
  message: string;
  sessionId?: string | null;
  agentId?: string;
  organizationId?: string;
  fileName?: string;
  fileContentBase64?: string;
  // Confirm-before-write flow: action keys the user has explicitly confirmed
  // for this turn (e.g. the write skill's action). Only ever set on a resend
  // after a ConfirmationRequest.
  confirmedActions?: string[];
  // Provenance of the turn, e.g. 'admin-test' from the admin Test panels. The
  // backend forwards it and syncedAI stamps AIRuns.ConfigSource — but only for
  // Admin/SuperAdmin callers — so test traffic is identifiable and excludable
  // from billing/credit reporting.
  source?: string;
}

// Returned when a write skill needs confirmation before it executes. The UI
// renders a confirm card from this and, on confirm, resends the same turn with
// `confirmedActions: [action]`.
export interface ConfirmationRequest {
  prompt: string;
  action: string;
  parameters: Record<string, any>;
}

export interface RecordItem {
  id?: string;
  entityType?: string;
  title: string;
  url?: string | null;
  fields: Record<string, string>;
}

/** SD-1244: a Profit and Loss or Balance Sheet a tool returned (syncedAI's statement block). */
export interface FinancialStatement {
  report: string;
  organisation?: string | null;
  currency?: string | null;
  periods: { label?: string; from?: string; to?: string; asAt?: string }[];
  sections: {
    title: string | null;
    total: number[];
    groups: { title: string | null; total: number[]; accounts: { name: string; amounts: number[] }[] }[];
  }[];
  summary: { label: string; amounts: number[] }[];
  basis?: string | null;
  notes?: string[];
}

export type ContentBlock =
  | { type: 'text'; text: string }
  | { type: 'table'; table: { headers: string[]; rows: string[][] } }
  | { type: 'records'; records: RecordItem[] }
  | { type: 'statement'; statement: FinancialStatement };

/**
 * SD-1244: a tool call while a turn runs — its name for people, a short summary of what it looked
 * up ("Period: last month"; never ids or secrets), whether it worked and how long it took.
 */
export interface ToolActivity {
  phase: 'start' | 'end';
  tool: string;
  label: string;
  ok?: boolean | null;
  detail?: string;
  ms?: number;
}

/** SD-1244: where a turn's time went, in ms. Sent to super admins and admins only. */
export interface TurnTimings {
  signInMs?: number;
  routeMs?: number;
  setupMs?: number;
  lookupMs?: number;
  firstWordMs?: number | null;
  modelMs?: number | null;
  totalMs?: number;
  agentId?: string;
  model?: string;
}

/** SD-1244: why a turn had the Synced tools it had. Sent to super admins and admins only. */
export interface ToolsStatus {
  enabled: boolean;
  loaded: boolean;
  count: number;
  reason: string;
}

/** What a streamed turn reports back. Every handler is optional except onError and onDone. */
export interface AgentStreamHandlers {
  onChunk?: (text: string) => void;
  onBlocks?: (blocks: ContentBlock[]) => void;
  onDone: (sessionId: string, agentId?: string) => void;
  onError: (message: string, code?: string) => void;
  onConfirm?: (req: ConfirmationRequest, sessionId: string, agentId: string) => void;
  onTool?: (e: ToolActivity) => void;
  onContext?: (e: { organisationName?: string }) => void;
  onAgent?: (agentId: string) => void;
  onToolsStatus?: (status: ToolsStatus) => void;
  onTimings?: (timings: TurnTimings) => void;
}

export interface AgentStreamOptions {
  message: string;
  sessionId?: string | null;
  agentId?: string;
  confirmedActions?: string[];
  organizationIdOverride?: string;
  source?: string;
  /** Aborting stops the turn quietly: no onError. */
  signal?: AbortSignal;
}

export interface AgentStreamEvent {
  type: string;
  agentId?: string;
  fileName?: string;
  content?: string;
  blocks?: ContentBlock[];
  sessionId?: string;
  message?: string;
  detail?: string;
  data?: unknown;
  organisationName?: string;
  phase?: 'start' | 'end';
  tool?: string;
  label?: string;
  ok?: boolean | null;
  ms?: number;
  enabled?: boolean;
  loaded?: boolean;
  count?: number;
  reason?: string;
  routeMs?: number;
  setupMs?: number;
  lookupMs?: number;
  firstWordMs?: number | null;
  totalMs?: number;
  model?: string;
  modelMs?: number | null;
  signInMs?: number;
  // Confirm-before-write flow (event type 'requiresConfirmation').
  requiresConfirmation?: boolean;
  confirmationRequest?: ConfirmationRequest;
}

/** SD-1244: one of the user's earlier chats. */
export interface ChatSummary {
  sessionId: string;
  agentId: string;
  title: string;
  turnCount: number;
  firstAt: string;
  lastAt: string;
}

/** SD-1244: a turn of an earlier chat. */
export interface ChatTurnRecord {
  userMessage: string;
  assistantMessage: string;
  isError: boolean;
  createdAt: string;
  durationMs?: number;
  tools: { label: string; ok: boolean; ms?: number }[];
  /** The answer's content blocks as the chat showed them; absent for turns kept before blocks were. */
  blocks?: ContentBlock[] | null;
}

export interface ChatRecord {
  sessionId: string;
  agentId: string;
  title: string;
  turns: ChatTurnRecord[];
}

@Injectable({
  providedIn: 'root'
})
export class AgentChatService {

  private get baseUrl(): string {
    return this.configService.apiURL;
  }

  constructor(private authService: AuthService, private configService: AppConfigService) {}

  /** The positional form, kept for the admin agent test panel. */
  streamMessage(
    message: string,
    sessionId: string | null,
    onChunk: (text: string) => void,
    onBlocks: (blocks: ContentBlock[]) => void,
    onDone: (sessionId: string, agentId?: string) => void,
    onError: (message: string) => void,
    // Confirm-before-write flow. `onConfirm` fires when the backend returns a
    // requiresConfirmation event; `agentId`/`confirmedActions` are set on the
    // resend after the user confirms (so the turn does not re-route).
    onConfirm?: (req: ConfirmationRequest, sessionId: string, agentId: string) => void,
    agentId?: string,
    confirmedActions?: string[],
    // Optional org override — the admin Test panel tests a specific org's agent
    // rather than the logged-in user's selected org.
    organizationIdOverride?: string,
    // Provenance tag (e.g. 'admin-test'); forwarded to the backend for run
    // stamping. Omitted from the body when unset so normal turns are unchanged.
    source?: string
  ): Promise<void> {
    return this.stream(
      { message, sessionId, agentId, confirmedActions, organizationIdOverride, source },
      { onChunk, onBlocks, onDone, onError, onConfirm });
  }

  /** SD-1244: the user's earlier chats in the selected organisation, newest first. */
  async listChats(): Promise<ChatSummary[]> {
    const body = await this.getJson(`agent/chat/sessions`);
    return (body?.items ?? []) as ChatSummary[];
  }

  /** SD-1244: one of the user's earlier chats. */
  async getChat(sessionId: string): Promise<ChatRecord> {
    return await this.getJson(`agent/chat/sessions/${encodeURIComponent(sessionId)}`) as ChatRecord;
  }

  private async getJson(path: string): Promise<any> {
    const organizationId = localStorage.getItem('SelectedOrganization') || '';
    const separator = path.indexOf('?') >= 0 ? '&' : '?';
    // A request that hangs must not leave the chat waiting forever.
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 20000);
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}${separator}organizationId=${encodeURIComponent(organizationId)}`, {
        method: 'GET',
        credentials: 'include',
        signal: abort.signal,
      });
    } catch (err) {
      throw new Error(abort.signal.aborted ? 'That took too long. Please try again.' : 'Network error. Please try again.');
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) {
      throw new Error(res.status === 404 ? 'That chat could not be found.' : `Could not load your chats (${res.status}).`);
    }
    return res.json();
  }

  /** One chat turn, streamed. */
  async stream(options: AgentStreamOptions, handlers: AgentStreamHandlers): Promise<void> {
    const url = `${this.baseUrl}agent/chat/stream`;
    const organizationId = options.organizationIdOverride || localStorage.getItem('SelectedOrganization') || undefined;
    const body: AgentChatRequest = {
      message: options.message,
      sessionId: options.sessionId ?? undefined,
      organizationId,
      // Only include when set so a normal first turn stays unchanged (and the
      // end-user panel sends no agentId, letting the backend route by intent).
      ...(options.agentId ? { agentId: options.agentId } : {}),
      ...(options.confirmedActions && options.confirmedActions.length ? { confirmedActions: options.confirmedActions } : {}),
      ...(options.source ? { source: options.source } : {}),
    };
    const aborted = () => !!options.signal?.aborted;

    let res: Response;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // ✅ CRITICAL
        body: JSON.stringify(body),
        signal: options.signal,
      });
    } catch (err) {
      if (!aborted()) { handlers.onError('Network error. Please try again.', 'network'); }
      return;
    }

    if (!res.ok || !res.body) {
      // SD-1244: a refusal the backend explains (AI switched off, no access) -- say so, not a bare status.
      if (res.status === 403 || res.status === 401) {
        const refusal = await res.json().catch(() => null);
        if (refusal?.error) {
          handlers.onError(refusal.message || refusal.error_description || 'You do not have access to this here.', refusal.error);
          return;
        }
      }
      handlers.onError(`Error: ${res.status} ${res.statusText}`, 'http');
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    // Backend echoes the resolved agent (e.g. the Billing Agent) on events;
    // remember the last-seen id so onDone/onConfirm can report it even when the
    // terminating event itself omits it.
    let resolvedAgentId = options.agentId ?? '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) { break; }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) { continue; }
          let event: AgentStreamEvent;
          try {
            event = JSON.parse(line.slice(6));
          } catch {
            continue; // skip malformed JSON lines
          }
          if (event.agentId) { resolvedAgentId = event.agentId; }
          this.dispatch(event, resolvedAgentId, handlers);
        }
      }
    } catch (err) {
      if (!aborted()) { handlers.onError('The connection was interrupted. Please try again.', 'network'); }
    }
  }

  private dispatch(event: AgentStreamEvent, resolvedAgentId: string, handlers: AgentStreamHandlers): void {
    switch (event.type) {
      case 'context':
        handlers.onContext?.({ organisationName: event.organisationName });
        break;
      case 'agent_selected':
        if (event.agentId) { handlers.onAgent?.(event.agentId); }
        break;
      case 'tool':
        if (event.phase && event.label) {
          handlers.onTool?.({
            phase: event.phase, tool: event.tool ?? '', label: event.label, ok: event.ok,
            detail: event.detail || undefined, ms: typeof event.ms === 'number' ? event.ms : undefined,
          });
        }
        break;
      case 'tools_status':
        handlers.onToolsStatus?.({
          enabled: !!event.enabled, loaded: !!event.loaded, count: event.count ?? 0, reason: event.reason ?? '',
        });
        break;
      case 'timings':
        handlers.onTimings?.({
          signInMs: event.signInMs, routeMs: event.routeMs, setupMs: event.setupMs, lookupMs: event.lookupMs,
          firstWordMs: event.firstWordMs, modelMs: event.modelMs, totalMs: event.totalMs, agentId: event.agentId, model: event.model,
        });
        break;
      case 'chunk':
        handlers.onChunk?.(event.content ?? '');
        break;
      case 'structured_response':
        if (event.blocks) { handlers.onBlocks?.(event.blocks); }
        break;
      case 'requiresConfirmation':
        if (handlers.onConfirm && event.confirmationRequest) {
          handlers.onConfirm(event.confirmationRequest, event.sessionId ?? '', resolvedAgentId);
        }
        break;
      case 'done':
        handlers.onDone(event.sessionId ?? '', resolvedAgentId || undefined);
        break;
      case 'error':
        handlers.onError(event.message ?? 'An error occurred', 'server');
        break;
    }
  }
}
